import { h } from '@omni-torn/ui';
import {
  estimateTrustWord,
  keyMeets,
  parseSpies,
  rankTargets,
  SPY_MAX_BATCH,
  SPY_SHARE_HELP,
  SPY_SHARE_LABEL,
  SPY_SHARE_TERMS,
  SPY_TERMS,
  spiesForEveryone,
  spyFrom,
  spyWhose,
  TERMS_VERSION,
  type EstimateFindAnswer,
  type FactionEstimateAnswer,
  type ParsedSpy,
  type PlayerEstimateAnswer,
  type SpyEntry,
  type SpyLogAnswer,
  type SpyRecordAnswer,
} from '@omni-torn/shared';
import { navigateTo } from '../nav';
import { readBattleStats } from '../read';
import { server, session, torn } from '../session';
import { ago, box, ext, facts, kids, loading, num, problem, profileUrl, short, table } from '../ui';
import { savePrefs } from './shared';
import { day, ESTIMATE_NOTE, estimateHistoryBox, estimateTiles, lastAction, methodWord, statusText } from './estimate-view';

/**
 * Spies & estimates (replacing TornStats' spy log, "spy on a user / a whole faction" and "store spy"):
 *  - a player: Omni-Torn's best estimate (how it was worked out, how old, how far to trust it), its history, what Torn
 *    shows publicly (level, faction, status, last action) and the spies of them you and your faction recorded or that
 *    were shared with every Omni-Torn user ("an Omni-Torn user's" — never who);
 *  - a faction: every member the database places there, sortable, with "good targets for you" worked out here in your
 *    browser from your own battle stats (read from Torn with your key, or typed) — they never leave this page;
 *  - recording spies: paste Torn's spy result (the job special's text) — kept for you and your faction (and, with "Share
 *    my spies with every Omni-Torn user" on — spyShareSwitch, from SPY_SHARE_TERMS — for every Omni-Torn user), its total
 *    (2 significant figures) feeds Omni-Torn's estimates. Needs terms version SPY_TERMS.
 * Server: apps/api/src/routes/spies.ts (invite-only reads; never fights or who sent what).
 */

let view: HTMLElement;

export async function renderSpies(v: HTMLElement, path: string): Promise<void> {
  const holder = h('div', {});
  v.replaceChildren(h('div', { class: 'page-head' }, h('h1', {}, 'Spies & estimates'), h('p', { class: 'muted' }, 'Omni-Torn’s estimates of other players, the spies you and your faction recorded, and spies Omni-Torn users shared with everyone.')), searchBar(), holder);
  view = holder;
  const player = /^\/spies\/player\/(\d+)\/?$/.exec(path)?.[1];
  const faction = /^\/spies\/faction\/(\d+)\/?$/.exec(path)?.[1];
  if (player) return renderPlayer(Number(player));
  if (faction) return renderFaction(Number(faction));
  holder.replaceChildren(recordBox(), await spyLogBox());
}

// ---------------------------------------------------------------- search

function searchBar(): HTMLElement {
  const input = h('input', { id: 'spy-search', type: 'search', placeholder: 'Player name or id…', maxlength: '40', autocomplete: 'off', 'aria-label': 'Player name or id, or faction id' }) as HTMLInputElement;
  const kind = h('select', { id: 'spy-kind', 'aria-label': 'Look up' }, h('option', { value: 'player' }, 'Player'), h('option', { value: 'faction' }, 'Faction')) as HTMLSelectElement;
  const found = h('ul', { class: 'suggestions', id: 'spy-found', hidden: true }) as HTMLUListElement;
  kind.addEventListener('change', () => (input.placeholder = kind.value === 'faction' ? 'Faction id…' : 'Player name or id…'));
  const form = h('form', { class: 'search', role: 'search', autocomplete: 'off' }, kind, input, h('button', { type: 'submit' }, 'Look up'), found);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const q = input.value.trim();
    const id = /^\[?(\d+)\]?$/.exec(q)?.[1] ?? /\[(\d+)\]\s*$/.exec(q)?.[1] ?? /(?:XID|ID)=(\d+)/i.exec(q)?.[1];
    found.hidden = true;
    if (id) return navigateTo(`/spies/${kind.value}/${id}`);
    if (kind.value === 'faction') {
      found.replaceChildren(h('li', { class: 'muted empty' }, 'Type the faction’s id (the number in its Torn address).'));
      found.hidden = false;
      return;
    }
    if (!/^[\w-]{1,30}$/.test(q)) return;
    try {
      const r = await server<EstimateFindAnswer>('GET', `/api/estimates/find?name=${encodeURIComponent(q)}`);
      if (r.players.length === 1 && r.players[0]!.name.toLowerCase() === q.toLowerCase()) return navigateTo(`/spies/player/${r.players[0]!.id}`);
      found.replaceChildren(...(r.players.length ? r.players.map((p) => h('li', {}, h('a', { href: `/spies/player/${p.id}`, 'data-link': true }, p.name, h('span', { class: 'muted' }, ` [${p.id}]${p.level ? ` · Lv ${p.level}` : ''}`)))) : [h('li', { class: 'muted empty' }, `Omni-Torn doesn’t know a player called “${q}” yet — try their id.`)]));
      found.hidden = false;
    } catch (err) {
      found.replaceChildren(h('li', { class: 'muted empty' }, (err as Error).message));
      found.hidden = false;
    }
  });
  return h('div', { class: 'lookup-bar' }, form);
}

// ---------------------------------------------------------------- words

const spyStats = (s: SpyEntry) => `str ${short(s.strength)} · def ${short(s.defense)} · spd ${short(s.speed)} · dex ${short(s.dexterity)}`;

// ---------------------------------------------------------------- a player

async function renderPlayer(id: number): Promise<void> {
  view.replaceChildren(loading('Looking them up…'));
  let a: PlayerEstimateAnswer;
  try {
    a = await server<PlayerEstimateAnswer>('GET', `/api/estimates/player/${id}`);
  } catch (err) {
    view.replaceChildren(problem(err));
    return;
  }
  const p = a.player;
  document.title = `${p?.name ?? `Player ${id}`} · Spies & estimates · Omni-Torn`;
  const head = h('div', { class: 'spy-head' }, h('h2', {}, p?.name ? `${p.name} [${id}]` : `Player [${id}]`), ext(profileUrl(id), 'Open in Torn ↗'));
  if (!p && !a.spies.length) {
    view.replaceChildren(box('Not known yet', [head, h('p', { class: 'muted', 'data-testid': 'spy-unknown' }, 'Omni-Torn hasn’t seen this player yet. Their profile, a fight or a spy puts them in.')]));
    return;
  }
  const tiles = estimateTiles(a);
  const about = p
    ? facts([
        ['Level', p.level ? String(p.level) : '—'],
        ['Faction', p.factionId ? h('a', { href: `/spies/faction/${p.factionId}`, 'data-link': true }, `[${p.factionId}] — every member`) : p.factionId === 0 ? 'None' : '—'],
        ['Status', statusText(p)],
        ['Last action', lastAction(p.lastAction)],
        ['First seen', day(p.firstSeen)],
        ['Player page', h('a', { href: `/lookup/${id}`, 'data-link': true }, 'profile, activity, compare')],
      ])
    : null;
  view.replaceChildren(
    ...kids(
      box(head, kids(tiles, about, h('p', { class: 'muted' }, ESTIMATE_NOTE))),
      estimateHistoryBox(a),
      box('Spies (yours, your faction’s and shared ones)', spyTable(a.spies, () => void renderPlayer(id)), { id: 'player-spies' }),
    ),
  );
}

function spyTable(list: readonly SpyEntry[], again: () => void, withName = false): HTMLElement {
  const remove = (s: SpyEntry) => {
    const b = h('button', { class: 'btn-small', type: 'button', title: 'Remove this spy (yours)' }, 'Remove') as HTMLButtonElement;
    b.addEventListener('click', async () => {
      b.disabled = true;
      await server('DELETE', `/api/spies?target=${s.target}&at=${s.at}`).catch(() => null);
      again();
    });
    return b;
  };
  return table(
    list,
    [
      ...(withName ? [{ label: 'Player', get: (s: SpyEntry) => h('a', { href: `/spies/player/${s.target}`, 'data-link': true }, s.name ? `${s.name} [${s.target}]` : `[${s.target}]`), sort: (s: SpyEntry) => s.name ?? String(s.target) }] : []),
      { label: 'When', get: (s) => h('span', { title: new Date(s.at).toISOString().replace('T', ' ').slice(0, 16) + ' TCT' }, ago(s.at)), sort: (s) => s.at },
      { label: 'Total', get: (s) => h('span', { title: num(s.total) }, short(s.total)), sort: (s) => s.total, cls: 'r' },
      { label: 'Stats', get: (s) => spyStats(s) },
      { label: 'By', get: (s) => (s.mine && s.shared ? h('span', { title: 'Shared with every Omni-Torn user (never who recorded it)' }, 'you · shared') : spyFrom(s)) },
      { label: '', get: (s) => (s.mine ? remove(s) : null) },
    ],
    { empty: 'No spies of them yet.', initial: withName ? 1 : 0 },
  );
}

// ---------------------------------------------------------------- a faction

/** Your total for "good targets", kept only on this page (never sent, never stored). */
let yourTotal: number | null = null;

async function renderFaction(id: number): Promise<void> {
  view.replaceChildren(loading('Reading the faction…'));
  let a: FactionEstimateAnswer;
  try {
    a = await server<FactionEstimateAnswer>('GET', `/api/estimates/faction/${id}`);
  } catch (err) {
    view.replaceChildren(problem(err));
    return;
  }
  document.title = `Faction ${id} · Spies & estimates · Omni-Torn`;
  const holder = h('div', {});
  const status = h('p', { class: 'form-status', 'aria-live': 'polite' });
  const typed = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Your total, e.g. 1.2b', 'aria-label': 'Your battle stats total', size: '16' }) as HTMLInputElement;
  const mark = h('button', { class: 'btn-small', type: 'button', id: 'spy-good' }, 'Mark good targets for me') as HTMLButtonElement;
  const draw = () => holder.replaceChildren(membersTable(a, yourTotal));
  mark.addEventListener('click', async () => {
    const t = parseTotal(typed.value);
    if (t) {
      yourTotal = t;
      status.textContent = `Using ${short(t)} (typed) — it stays on this page.`;
      return draw();
    }
    // Your own battle stats, read from Torn with your key (Limited or better) — in this browser only.
    if (!keyMeets(session.get()?.access ?? null, { level: 3, user: ['battlestats'] })) {
      status.textContent = 'Type your battle stats total (your key can’t read them) — it stays on this page.';
      return;
    }
    mark.disabled = true;
    try {
      const bs = readBattleStats(await torn('/user/', { selections: 'battlestats' }));
      if (bs) {
        yourTotal = bs.total;
        status.textContent = `Using your battle stats from Torn (${short(bs.total)}) — they stay in this browser.`;
        draw();
      } else status.textContent = 'Torn didn’t send your battle stats — type your total instead.';
    } catch (err) {
      status.textContent = (err as Error).message;
    } finally {
      mark.disabled = false;
    }
  });
  draw();
  view.replaceChildren(
    box(
      h('div', { class: 'spy-head' }, h('h2', {}, `Faction [${id}]`), ext(`https://www.torn.com/factions.php?step=profile&ID=${id}`, 'Open in Torn ↗')),
      [
        h('p', { class: 'muted' }, a.members.length ? `${a.members.length} members Omni-Torn has seen in this faction. Click a header to sort.` : 'Omni-Torn hasn’t seen anyone in this faction yet — opening its page in Torn puts its members in.'),
        a.members.length ? h('div', { class: 'toolbar' }, mark, typed) : null,
        status,
        holder,
      ],
      { id: 'faction-members' },
    ),
  );
}

/** "1.2b", "850m", "1,234,567" → a number; nothing sensible → null. */
function parseTotal(raw: string): number | null {
  const m = /^\s*([\d,]+(?:\.\d+)?)\s*([kmbt])?\s*$/i.exec(raw);
  if (!m) return null;
  const n = Number(m[1]!.replace(/,/g, '')) * ({ k: 1e3, m: 1e6, b: 1e9, t: 1e12 }[(m[2] ?? '').toLowerCase() as 'k'] ?? 1);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function membersTable(a: FactionEstimateAnswer, mine: number | null): HTMLElement {
  const rows = mine ? rankTargets(a.members, mine) : a.members.map((m) => ({ ...m, ff: null as number | null, good: false }));
  return table(
    rows,
    [
      { label: 'Player', get: (m) => h('a', { href: `/spies/player/${m.id}`, 'data-link': true, class: m.good ? 'spy-good' : '' }, m.name ? `${m.name} [${m.id}]` : `[${m.id}]`, m.good ? h('span', { class: 'tag' }, 'good target') : null), sort: (m) => (m.name ?? '').toLowerCase() },
      { label: 'Level', get: (m) => (m.level ? String(m.level) : '—'), sort: (m) => m.level ?? 0, cls: 'r' },
      { label: 'Estimate', get: (m) => h('span', { title: m.total ? `${num(m.total)} — from ${methodWord(m.totalSource)}${m.totalAt ? `, ${ago(m.totalAt)}` : ''}` : '' }, short(m.total)), sort: (m) => m.total ?? -1, cls: 'r' },
      { label: 'Trust', get: (m) => (m.total ? estimateTrustWord(m.totalSource, m.totalAt ? Date.now() - m.totalAt : 0) : '—') },
      { label: 'Spy', get: (m) => { const s = a.spies[String(m.id)]; return s ? h('span', { title: `${spyStats(s)} — ${spyWhose(s)}` }, `${short(s.total)} (${ago(s.at)})`) : '—'; }, sort: (m) => a.spies[String(m.id)]?.total ?? -1, cls: 'r' },
      ...(mine ? [{ label: 'Fair fight', get: (m: (typeof rows)[number]) => (m.ff ? m.ff.toFixed(2) : '—'), sort: (m: (typeof rows)[number]) => m.ff ?? -1, cls: 'r' }] : []),
      { label: 'Status', get: (m) => statusText(m), sort: (m) => m.status ?? '' },
      { label: 'Last action', get: (m) => lastAction(m.lastAction), sort: (m) => m.lastAction ?? 0 },
    ],
    { empty: 'No members known.', initial: mine ? -1 : 2 },
  );
}

// ---------------------------------------------------------------- recording spies

function recordBox(): HTMLElement {
  const agreed = (session.me()?.prefs.terms ?? 0) >= SPY_TERMS;
  const text = h('textarea', { id: 'spy-paste', rows: '7', placeholder: 'Name: Someone [1234567]\nLevel: 50\nYou managed to get the following results:\nStrength: 1,234,567\nSpeed: …\nDexterity: …\nDefense: …\nTotal: …', 'aria-label': 'Paste spy results' }) as HTMLTextAreaElement;
  const preview = h('div', { 'data-testid': 'spy-preview' });
  const status = h('p', { class: 'form-status', 'aria-live': 'polite' });
  const keep = h('button', { class: 'btn-primary', type: 'button', id: 'spy-keep', disabled: true }, 'Keep these spies') as HTMLButtonElement;
  let parsed: ParsedSpy[] = [];
  const targets = new Map<number, HTMLInputElement>();
  const read = () => {
    parsed = parseSpies(text.value).slice(0, SPY_MAX_BATCH);
    targets.clear();
    preview.replaceChildren(
      ...kids(
      parsed.length
        ? table(
            parsed.map((s, i) => ({ s, i })),
            [
              {
                label: 'Player',
                get: ({ s, i }) => {
                  if (s.target) return s.name ? `${s.name} [${s.target}]` : `[${s.target}]`;
                  const input = h('input', { type: 'text', inputmode: 'numeric', placeholder: 'Their id', size: '10', 'aria-label': 'Player id' }) as HTMLInputElement;
                  targets.set(i, input);
                  return h('span', {}, s.name ? `${s.name} ` : '', input);
                },
              },
              { label: 'When', get: ({ s }) => (Date.now() - s.at < 60_000 ? 'now' : ago(s.at)) },
              { label: 'Total', get: ({ s }) => `${short(s.total)}${s.approx ? ' (rounded)' : ''}`, cls: 'r' },
              { label: 'Stats', get: ({ s }) => `str ${short(s.strength)} · def ${short(s.defense)} · spd ${short(s.speed)} · dex ${short(s.dexterity)}${s.derived ? ` (${s.derived} worked out)` : ''}` },
            ],
          )
        : text.value.trim()
          ? h('p', { class: 'muted' }, 'No spy found in that — paste the whole result, with the stat names.')
          : null,
      ),
    );
    keep.disabled = !parsed.length || !agreed;
  };
  text.addEventListener('input', read);
  keep.addEventListener('click', async () => {
    const list = parsed
      .map((s, i) => {
        const t = s.target ?? Number(targets.get(i)?.value.trim().replace(/^\[|\]$/g, ''));
        const { derived: _d, approx: _a, ...spy } = s;
        return Number.isInteger(t) && t > 0 ? { ...spy, target: t } : null;
      })
      .filter((x) => x !== null);
    if (!list.length) {
      status.className = 'form-status bad';
      status.textContent = 'Say whose spy it is (their id).';
      return;
    }
    keep.disabled = true;
    try {
      const r = await server<SpyRecordAnswer>('POST', '/api/spies', { spies: list });
      status.className = `form-status ${r.ok ? 'ok' : 'bad'}`;
      status.textContent = r.ok ? `Kept ${r.kept}${r.skipped ? ` (${r.skipped} not: already kept, about you, or the numbers don’t add up)` : ''} — for you and your faction${r.shared ? `; ${r.shared} shared with every Omni-Torn user` : ''}.` : (r.message ?? 'Not kept.');
      if (r.ok && r.kept) {
        text.value = '';
        read();
        const log = await spyLogBox();
        document.querySelector('[data-card=spy-log]')?.replaceWith(log);
      }
    } catch (err) {
      status.className = 'form-status bad';
      status.textContent = (err as Error).message;
    } finally {
      keep.disabled = !parsed.length || !agreed;
    }
  });
  const keptFor = () => h('p', { class: 'muted', 'data-testid': 'spy-kept-for' }, spiesForEveryone(session.me()?.prefs ?? { terms: 0 }) ? 'Kept for you and your faction, and shared with every Omni-Torn user (never who recorded it). ' : 'Kept for you and your faction (never anyone else). ', 'The total, rounded, also goes into Omni-Torn’s estimate of that player — the most trusted kind.');
  let kept = keptFor();
  const note = agreed
    ? kept
    : h(
        'p',
        { class: 'notice', 'data-testid': 'spy-terms' },
        ...(TERMS_VERSION >= SPY_TERMS
          ? [`Keeping spies shares them with your faction: it opens once you agree to Omni-Torn’s terms (version ${SPY_TERMS}) — continuing to use Omni-Torn after the “terms changed” banner agrees.`]
          : [`Keeping spies opens with Omni-Torn’s terms version ${SPY_TERMS} (they say spies go to your faction). You can read a spy here already; keeping it comes then — see `, h('a', { href: '/account', 'data-link': true }, 'Account'), '.']),
      );
  const share = spyShareSwitch(() => {
    if (!agreed) return;
    const next = keptFor();
    kept.replaceWith(next);
    kept = next;
  });
  return box('Record a spy', [h('p', {}, 'Paste the result of a spy you did yourself — the job special’s text from Torn (several at once is fine). Not spies copied from other tools: their terms keep those with them. Each one shows below before anything is kept.'), text, preview, note, h('div', { class: 'toolbar' }, keep), status, share], { id: 'spy-record' });
}

/**
 * "Share my spies with every Omni-Torn user" (WebPrefs `shareSpies`, synced: the extension follows it too) — off unless
 * switched on, from terms SPY_SHARE_TERMS. On the Spies page and in Account.
 */
export function spyShareSwitch(changed: () => void = () => {}): HTMLElement {
  const prefs = session.me()?.prefs;
  const can = (prefs?.terms ?? 0) >= SPY_SHARE_TERMS;
  const status = h('span', { class: 'save-status', role: 'status' });
  const toggle = h('input', { type: 'checkbox', id: 'spy-share', checked: !!prefs && spiesForEveryone(prefs), disabled: !can }) as HTMLInputElement;
  toggle.addEventListener('change', async () => {
    status.className = 'save-status';
    status.textContent = 'Saving…';
    try {
      const p = await savePrefs({ shareSpies: toggle.checked });
      toggle.checked = spiesForEveryone(p);
      status.className = 'save-status ok';
      status.textContent = toggle.checked ? 'On — spies you record from now on are shared with every Omni-Torn user.' : 'Off — spies you record from now on stay with you and your faction (ones already shared stay shared).';
      changed();
    } catch (err) {
      toggle.checked = !toggle.checked;
      status.className = 'save-status bad';
      status.textContent = (err as Error).message;
    }
  });
  return h(
    'div',
    { class: 'spy-share', 'data-testid': 'spy-share' },
    h('label', { class: 'switch' }, toggle, h('strong', {}, ` ${SPY_SHARE_LABEL}`), status),
    h('p', { class: 'muted' }, SPY_SHARE_HELP),
    can ? null : h('p', { class: 'notice' }, `This opens with Omni-Torn’s terms version ${SPY_SHARE_TERMS} — continuing to use Omni-Torn after the “terms changed” banner agrees to them.`),
  );
}

async function spyLogBox(offset = 0): Promise<HTMLElement> {
  let a: SpyLogAnswer;
  try {
    a = await server<SpyLogAnswer>('GET', `/api/spies/log?offset=${offset}`);
  } catch (err) {
    return box('Spy log', problem(err), { id: 'spy-log' });
  }
  const more = a.more ? h('button', { class: 'btn-small', type: 'button' }, 'Older') : null;
  const newer = offset > 0 ? h('button', { class: 'btn-small', type: 'button' }, 'Newer') : null;
  const again = async (to: number) => document.querySelector('[data-card=spy-log]')?.replaceWith(await spyLogBox(to));
  more?.addEventListener('click', () => void again(offset + a.spies.length));
  newer?.addEventListener('click', () => void again(Math.max(0, offset - 50)));
  return box('Spy log (yours and your faction’s)', [spyTable(a.spies, () => void again(offset), true), more || newer ? h('div', { class: 'toolbar' }, newer, more) : null], { id: 'spy-log' });
}

